import { memo, useCallback } from 'react';
import styles from './TablePagination.module.scss';
import IconButton from '../../../IconButton/ui/IconButton';

interface TablePaginationProps {
  maxPages: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * @param props - The props for the component.
 * @param maxPages - Maximum pages count for table pagination
 * @param currentPage - Current page number in table pagination
 * @param setCurrentPage - Function which is called after changing page
 * @returns A React component that displays the table pagination.
 */

function TablePaginationComponent(props: TablePaginationProps) {
  const { currentPage, maxPages, setCurrentPage } = props;

  const createPageButton = useCallback(
    (page: number, active: boolean) => (
      <button
        type="button"
        key={page}
        className={[
          styles.pageButton,
          active ? styles.activeButton : undefined,
        ].join(' ')}
        onClick={() => setCurrentPage(page - 1)}
      >
        <span>{page}</span>
      </button>
    ),
    [setCurrentPage]
  );

  const renderPageButtons = useCallback(() => {
    const pages: JSX.Element[] = [];

    // Determine the range of page numbers to display
    let startRange: number;
    let endRange: number;

    if (maxPages <= 5 || currentPage < 3) {
      // If there are 5 or fewer pages, or if the current page is less than 3, show the first 5
      startRange = 0;
      endRange = Math.min(5, maxPages) - 1;
    } else if (currentPage > maxPages - 4) {
      // If the current page is among the last 5, show the last 5
      startRange = maxPages - 5;
      endRange = maxPages - 1;
    } else {
      // Otherwise, show the current page in the middle
      startRange = currentPage - 1;
      endRange = currentPage + 1;
    }

    // Create the page buttons
    if (startRange > 0) {
      pages.push(createPageButton(1, false)); // First page
      if (startRange > 1) {
        pages.push(
          <p key="start-ellipsis" className={[styles.pageButton].join(' ')}>
            ...
          </p>
        ); // Ellipsis
      }
    }

    for (let i = startRange; i <= endRange; i += 1) {
      pages.push(createPageButton(i + 1, i === currentPage)); // Middle range pages
    }

    if (endRange < maxPages - 1) {
      if (endRange < maxPages - 2) {
        pages.push(
          <p key="end-ellipsis" className={[styles.pageButton].join(' ')}>
            ...
          </p>
        ); // Ellipsis
      }
      pages.push(createPageButton(maxPages, false)); // Last page
    }

    return pages;
  }, [createPageButton, currentPage, maxPages]);

  return (
    <div className={styles.tablePagination}>
      <IconButton
        className={[
          styles.btn,
          currentPage + 1 > 1 ? undefined : styles.hidden,
        ].join(' ')}
        onClick={() => setCurrentPage((prev) => prev - 1)}
      >
        <img src="/icons/prev.svg" alt="" />
        Prev
      </IconButton>

      <div className={styles.pageButtons}>{renderPageButtons()}</div>

      <IconButton
        className={[
          styles.btn,
          currentPage < maxPages - 1 ? undefined : styles.hidden,
        ].join(' ')}
        onClick={() => setCurrentPage((prev) => prev + 1)}
      >
        Next
        <img src="/icons/next.svg" alt="" />
      </IconButton>
    </div>
  );
}

const TablePagination = memo(TablePaginationComponent);

export default TablePagination;
